/**
 * Portal-side handoff intercept.
 *
 * On every page load, look for `?handoff=<jwt>` in the URL. When
 * present:
 *   1. Exchange the handoff JWT for a full session JWT via the
 *      backend's `/auth/handoff-exchange` endpoint.
 *   2. Persist the session JWT through `authService` (so the rest of
 *      the portal's existing axios interceptor flow finds it).
 *   3. Strip the `handoff` query param via `history.replaceState`
 *      IMMEDIATELY so it doesn't persist in browser history, share
 *      buttons, or `Referer` headers.
 *
 * This hook is the only place that should ever observe the handoff
 * query string — every other consumer reads `authService.getTokens()`
 * exactly the way it does for normal login.
 *
 * UX:
 *   - When the param is present and exchange is in flight, return
 *     `{ status: 'exchanging' }`. The caller renders a spinner so
 *     private routes don't flash "Please sign in".
 *   - On success → `{ status: 'idle' }` (user is now logged in).
 *   - On failure → `{ status: 'error', error }`. We strip the query
 *     param anyway (don't want to leave a broken token in the URL)
 *     and surface the error so the page can show a banner.
 */

import { useEffect, useState } from 'react';
import authService from '../services/authService';

const HANDOFF_QUERY_PARAM = 'handoff';

export type HandoffStatus =
  | { status: 'idle' }
  | { status: 'exchanging' }
  | { status: 'error'; error: string };

function readAndStripHandoff(): string | null {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const token = url.searchParams.get(HANDOFF_QUERY_PARAM);
  if (!token) return null;
  // Always strip — even on the error path — so the broken token
  // doesn't linger in browser history or get exfiltrated via
  // referrer headers when the user clicks an external link.
  url.searchParams.delete(HANDOFF_QUERY_PARAM);
  const clean = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash;
  window.history.replaceState(window.history.state, '', clean);
  return token;
}

export function useHandoffIntercept(): HandoffStatus {
  // Synchronously check the URL on the very first render so a
  // private-route guard can see `exchanging` immediately rather than
  // flashing the login page for one frame.
  const initialToken = typeof window !== 'undefined' ? readAndStripHandoff() : null;
  const [state, setState] = useState<HandoffStatus>(
    initialToken ? { status: 'exchanging' } : { status: 'idle' }
  );

  useEffect(() => {
    if (!initialToken) return;
    let cancelled = false;
    (async () => {
      try {
        await authService.acceptHandoff(initialToken);
        if (!cancelled) setState({ status: 'idle' });
      } catch (err: any) {
        if (cancelled) return;
        const msg = err?.response?.data?.error || err?.message || 'Could not sign you in from the app';
        setState({ status: 'error', error: msg });
      }
    })();
    return () => { cancelled = true; };
    // initialToken is captured at mount — don't re-run on later renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
